import { useEffect, useRef } from "react";

export interface LazySerializer<T> {
  cancel: () => void;
  flush: () => Promise<void>;
  schedule: (value: T) => void;
}

export function createLazySerializer<T>(serialize: (value: T) => void | Promise<void>, delayMs = 250): LazySerializer<T> {
  let pending: T | undefined;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let tail = Promise.resolve();

  const enqueue = (value: T) => {
    const write = tail.then(() => serialize(value));
    tail = write.then(() => undefined, () => undefined);
    return write;
  };

  const cancel = () => {
    if (timer !== undefined) clearTimeout(timer);
    timer = undefined;
    pending = undefined;
  };

  const flush = () => {
    if (timer !== undefined) clearTimeout(timer);
    timer = undefined;
    if (pending === undefined) return tail;
    const value = pending;
    pending = undefined;
    return enqueue(value);
  };

  const schedule = (value: T) => {
    pending = value;
    if (timer !== undefined) clearTimeout(timer);
    timer = setTimeout(() => {
      void flush().catch(() => undefined);
    }, delayMs);
  };

  return { cancel, flush, schedule };
}

export function useLazySerializer<T>(serialize: (value: T) => void | Promise<void>, delayMs = 250): LazySerializer<T> {
  const serializeRef = useRef(serialize);
  serializeRef.current = serialize;
  const serializerRef = useRef<LazySerializer<T>>(undefined);
  if (!serializerRef.current) {
    serializerRef.current = createLazySerializer((value) => serializeRef.current(value), delayMs);
  }

  useEffect(() => () => {
    void serializerRef.current?.flush().catch(() => undefined);
  }, []);

  return serializerRef.current;
}
